import {AlertTriangleIcon, CheckIcon, Loader2Icon} from "lucide-react";
import {useCallback, useEffect, useState} from "react";

import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Input} from "@components/ui/input";
import {Label} from "@components/ui/label";
import {
    previewPromoteSandboxInsight,
    promoteSandboxInsight,
    type SandboxInsight,
} from "@hooks/use-vnext";

interface SandboxPromotionDialogProps {
    workspaceId: string;
    insight: SandboxInsight;
    onClose: () => void;
    onPromoted: () => void;
}

export function SandboxPromotionDialog({
    workspaceId,
    insight,
    onClose,
    onPromoted,
}: SandboxPromotionDialogProps) {
    const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
    const [loading, setLoading] = useState(true);
    const [promoting, setPromoting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notes, setNotes] = useState("");

    const loadPreview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await previewPromoteSandboxInsight(workspaceId, insight.id);
            setPreview(result.preview);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load preview");
        } finally {
            setLoading(false);
        }
    }, [workspaceId, insight.id]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    const handlePromote = async () => {
        setPromoting(true);
        setError(null);
        try {
            await promoteSandboxInsight(workspaceId, insight.id, notes);
            onPromoted();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Promotion failed");
            setPromoting(false);
        }
    };

    const proposed = insight.proposed_op_json || {};

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <Card className="w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
                <CardContent className="p-5 space-y-4">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                            Promote Insight to Model
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{insight.title}</p>
                    </div>

                    {/* Proposed Op Details */}
                    <div className="rounded border bg-gray-50 p-3 space-y-1.5 text-xs">
                        <div className="font-medium text-gray-700">Proposed Operation</div>
                        <div className="flex gap-4">
                            <span className="text-gray-500">Type:</span>
                            <span className="font-mono">
                                {(proposed.op_type as string) || "—"}
                            </span>
                        </div>
                        {!!proposed.target_node_id && (
                            <div className="flex gap-4">
                                <span className="text-gray-500">Target:</span>
                                <span className="font-mono text-[10px]">
                                    {String(proposed.target_node_id)}
                                </span>
                            </div>
                        )}
                        {!!proposed.params && (
                            <div className="flex gap-4">
                                <span className="text-gray-500">Params:</span>
                                <span className="font-mono text-[10px]">
                                    {JSON.stringify(proposed.params)}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Preview Deltas */}
                    {loading && (
                        <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500">
                            <Loader2Icon className="h-4 w-4 animate-spin" />
                            Loading preview...
                        </div>
                    )}

                    {!loading && preview && (
                        <div className="rounded border p-3 space-y-1 text-xs">
                            <div className="font-medium text-gray-700">Model Impact</div>
                            <pre className="text-[10px] font-mono text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
                                {JSON.stringify(preview, null, 2)}
                            </pre>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center gap-2 rounded bg-red-50 px-3 py-2 text-xs text-red-700">
                            <AlertTriangleIcon className="h-3 w-3 shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Notes */}
                    <div>
                        <Label className="text-xs text-gray-600">Notes (optional)</Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add a note about this promotion..."
                            className="mt-1 text-xs"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            disabled={promoting}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            className="bg-[#5BF6C8] text-gray-900 hover:bg-[#4DE5B7]"
                            onClick={handlePromote}
                            disabled={loading || promoting || !!error}
                        >
                            {promoting ? (
                                <>
                                    <Loader2Icon className="h-3 w-3 animate-spin mr-1" />
                                    Promoting...
                                </>
                            ) : (
                                <>
                                    <CheckIcon className="h-3 w-3 mr-1" />
                                    Confirm Promote
                                </>
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
